import type {
  IceCandidatePayload,
  PeerLeaveReason,
  PeerRole,
  ProtocolPayloads,
} from '@screenshare/protocol';
import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { NOOP_LOGGER, writeLog, type StructuredLogger } from './logger';
import { nextWebRtcState, type WebRtcStateCause } from './state';
import {
  WebRtcError,
  type IceRestartOptions,
  type IceRestartResult,
  type LocalMediaSource,
  type LocalMediaTracks,
  type PeerConnectionFactory,
  type PeerIceMessage,
  type PeerSignalMessage,
  type PeerSignalingChannel,
  type WebRtcErrorCode,
  type WebRtcEvents,
  type WebRtcLifecycleState,
} from './types';

const MAX_PENDING_CANDIDATES = 256;
const MAX_IGNORED_NEGOTIATIONS = 32;

export interface PeerConnectionManagerOptions {
  readonly role: PeerRole;
  readonly signaling: PeerSignalingChannel;
  readonly configuration: RTCConfiguration;
  readonly logger?: StructuredLogger;
  readonly createPeerConnection?: PeerConnectionFactory;
  readonly createNegotiationId?: () => string;
}

export class PeerConnectionManager extends TypedEventEmitter<WebRtcEvents> {
  private readonly logger: StructuredLogger;
  private readonly createPeerConnection: PeerConnectionFactory;
  private readonly createNegotiationId: () => string;
  private readonly senders = new Map<LocalMediaSource, RTCRtpSender>();
  private readonly pendingCandidates = new Map<string, Array<IceCandidatePayload | null>>();
  private readonly ignoredNegotiations = new Set<string>();
  private readonly acceptedRemoteNegotiations = new Set<string>();
  private readonly localIceUfrags = new Map<string, string>();
  private peerConnection: RTCPeerConnection | null = null;
  private unsubscribeSignaling: (() => void) | null = null;
  private makingOffer = false;
  private negotiationPending = false;
  private remoteReady = false;
  private ignoreOffer = false;
  private isSettingRemoteAnswerPending = false;
  private localOfferId: string | null = null;
  private localIceNegotiationId: string | null = null;
  private remoteDescriptionId: string | null = null;
  private signalQueue: Promise<void> = Promise.resolve();
  private closePromise: Promise<void> | null = null;
  private iceRestartPromise: Promise<IceRestartResult> | null = null;
  private peerRefreshRecoveryPending = false;
  private offerGeneration = 0;
  private remoteSignalingEpoch = -1;
  private _state: WebRtcLifecycleState = 'idle';

  constructor(private readonly options: PeerConnectionManagerOptions) {
    super();
    this.logger = options.logger ?? NOOP_LOGGER;
    this.createPeerConnection =
      options.createPeerConnection ?? ((configuration) => new RTCPeerConnection(configuration));
    this.createNegotiationId = options.createNegotiationId ?? (() => crypto.randomUUID());
  }

  get state(): WebRtcLifecycleState {
    return this._state;
  }

  get connection(): RTCPeerConnection | null {
    return this.peerConnection;
  }

  start(media: LocalMediaTracks = {}): void {
    if (this._state !== 'idle') {
      throw new WebRtcError('invalid-state', `Cannot start peer connection from ${this._state}.`);
    }
    validateLocalMedia(media);
    this.transition('start');

    try {
      const connection = this.createPeerConnection(this.options.configuration);
      this.peerConnection = connection;
      this.addConnectionListeners(connection);
      this.attachInitialMedia(connection, media);
      this.unsubscribeSignaling = this.options.signaling.subscribe(this.handleSignal);
      this.announceReady();
      writeLog(this.logger, 'info', 'webrtc', 'peer.started', {
        role: this.options.role,
        iceServerCount: this.options.configuration.iceServers?.length ?? 0,
      });
    } catch (cause) {
      this.fail('signaling-failed', 'Could not start peer connection.', cause, true);
      void this.close({ notifyPeer: false });
      throw cause;
    }
  }

  async setLocalMedia(media: LocalMediaTracks): Promise<void> {
    validateLocalMedia(media);
    const connection = this.requireConnection();
    await this.syncTrack(connection, 'video', media.video, media.screenStream);
    await this.syncTrack(connection, 'screen-audio', media.screenAudio, media.screenStream);
    await this.syncTrack(connection, 'microphone', media.microphone, media.microphoneStream);
  }

  async setLocalTrack(
    source: LocalMediaSource,
    track?: MediaStreamTrack,
    stream?: MediaStream,
  ): Promise<void> {
    await this.syncTrack(this.requireConnection(), source, track, stream);
  }

  getSender(source: LocalMediaSource): RTCRtpSender | null {
    return this.senders.get(source) ?? null;
  }

  updateIceServers(iceServers: readonly RTCIceServer[]): void {
    const connection = this.requireConnection();
    const configuration = connection.getConfiguration();
    connection.setConfiguration({
      ...configuration,
      iceServers: iceServers.map((server) => ({
        ...server,
        urls: typeof server.urls === 'string' ? server.urls : [...server.urls],
      })),
    });
    writeLog(this.logger, 'info', 'webrtc', 'ice-servers.updated', {
      iceServerCount: iceServers.length,
    });
  }

  announceReady(): void {
    if (this._state === 'closed') {
      throw new WebRtcError('invalid-state', 'Cannot announce a closed peer connection.');
    }
    this.options.signaling.send('peer.ready', { role: this.options.role });
  }

  restartIce(options: IceRestartOptions = {}): Promise<IceRestartResult> {
    if (this.iceRestartPromise !== null) return this.iceRestartPromise;
    const operation = Promise.resolve()
      .then(() => {
        const connection = this.requireConnection();
        const relayForced = options.forceRelay === true && configureRelayOnly(connection);
        connection.restartIce();
        this.negotiationPending = true;
        this.maybeMakeOffer();
        writeLog(this.logger, 'info', 'webrtc', 'ice.restart-requested', { relayForced });
        return Object.freeze({ relayForced });
      })
      .finally(() => {
        if (this.iceRestartPromise === operation) this.iceRestartPromise = null;
      });
    this.iceRestartPromise = operation;
    return operation;
  }

  async close(
    options: { readonly notifyPeer?: boolean; readonly reason?: PeerLeaveReason } = {},
  ): Promise<void> {
    if (this.closePromise !== null) {
      return this.closePromise;
    }
    this.closePromise = this.performClose(options);
    return this.closePromise;
  }

  private readonly handleSignal = (message: PeerSignalMessage) => {
    this.signalQueue = this.signalQueue
      .then(() => this.processSignal(message))
      .catch((cause: unknown) => {
        this.fail('signaling-failed', 'Could not process peer signaling.', cause, true);
      });
  };

  private async processSignal(message: PeerSignalMessage): Promise<void> {
    if (this._state === 'closed') {
      return;
    }
    if (message.epoch < this.remoteSignalingEpoch) {
      writeLog(this.logger, 'warn', 'webrtc', 'signal.ignored', {
        type: message.type,
        epoch: message.epoch,
        reason: 'stale-epoch',
      });
      return;
    }
    if (message.epoch > this.remoteSignalingEpoch) {
      this.remoteSignalingEpoch = message.epoch;
      this.remoteDescriptionId = null;
      this.pendingCandidates.clear();
      this.ignoredNegotiations.clear();
      this.acceptedRemoteNegotiations.clear();
      this.ignoreOffer = false;
    }
    switch (message.type) {
      case 'peer.offer':
        await this.handleOffer(message.payload);
        return;
      case 'peer.answer':
        await this.handleAnswer(message.payload);
        return;
      case 'peer.ice':
        await this.handleRemoteCandidate(message);
        return;
      case 'peer.ready':
        if (message.payload.role !== this.options.role) {
          const wasReady = this.remoteReady;
          this.remoteReady = true;
          this.emit('peerready', message.payload);
          const connectionState = this.peerConnection?.connectionState;
          const establishedPeerReturned =
            wasReady &&
            (connectionState === 'connected' ||
              connectionState === 'disconnected' ||
              connectionState === 'failed');
          if (establishedPeerReturned && !this.peerRefreshRecoveryPending) {
            this.peerRefreshRecoveryPending = true;
            this.emit('peerreset', message.payload);
            void this.restartIce().catch((cause: unknown) => {
              this.fail(
                'negotiation-failed',
                'Could not reconnect the returning peer.',
                cause,
                true,
              );
            });
          } else {
            this.maybeMakeOffer();
          }
        }
        return;
      case 'peer.leave':
        this.emit('peerleft', message.payload);
        if (
          this.options.role === 'host' &&
          (message.payload.reason === 'left' || message.payload.reason === 'kicked')
        ) {
          this.remoteReady = false;
          this.peerRefreshRecoveryPending = false;
          return;
        }
        await this.close({ notifyPeer: false });
        return;
    }
  }

  private readonly handleNegotiationNeeded = () => {
    this.negotiationPending = true;
    this.maybeMakeOffer();
  };

  private maybeMakeOffer(): void {
    const connection = this.peerConnection;
    if (
      !this.negotiationPending ||
      !this.remoteReady ||
      this.makingOffer ||
      this._state === 'closed' ||
      connection?.signalingState !== 'stable'
    ) {
      return;
    }
    void this.makeOffer().catch((cause: unknown) => {
      this.fail('negotiation-failed', 'Could not create a WebRTC offer.', cause, true);
    });
  }

  private async makeOffer(): Promise<void> {
    if (
      this.makingOffer ||
      !this.negotiationPending ||
      !this.remoteReady ||
      this._state === 'closed'
    ) {
      return;
    }
    const connection = this.requireConnection();
    this.negotiationPending = false;
    this.makingOffer = true;
    const negotiationId = this.createNegotiationId();
    const generation = ++this.offerGeneration;
    this.localOfferId = negotiationId;
    this.localIceNegotiationId = negotiationId;
    this.transition('negotiation');
    try {
      await connection.setLocalDescription();
      if (!this.isActiveConnection(connection) || generation !== this.offerGeneration) {
        return;
      }
      const description = connection.localDescription;
      if (description?.type !== 'offer' || !description.sdp) {
        throw new Error('Browser did not produce an offer description.');
      }
      this.rememberLocalIceUfrags(description.sdp, negotiationId);
      this.options.signaling.send('peer.offer', { negotiationId, sdp: description.sdp });
      writeLog(this.logger, 'debug', 'webrtc', 'offer.sent', { negotiationId });
    } finally {
      this.makingOffer = false;
    }
  }

  private async handleOffer(payload: ProtocolPayloads['peer.offer']): Promise<void> {
    const connection = this.requireConnection();
    if (this.acceptedRemoteNegotiations.has(payload.negotiationId)) {
      writeLog(this.logger, 'warn', 'webrtc', 'offer.ignored', {
        negotiationId: payload.negotiationId,
        reason: 'duplicate',
      });
      return;
    }
    this.remoteReady = true;
    const readyForOffer =
      !this.makingOffer &&
      (connection.signalingState === 'stable' || this.isSettingRemoteAnswerPending);
    const offerCollision = !readyForOffer;
    const polite = this.options.role === 'viewer';
    this.ignoreOffer = !polite && offerCollision;

    if (this.ignoreOffer) {
      this.rememberIgnoredNegotiation(payload.negotiationId);
      writeLog(this.logger, 'warn', 'webrtc', 'offer.ignored', {
        negotiationId: payload.negotiationId,
        reason: 'collision',
      });
      return;
    }

    this.transition('negotiation');
    try {
      this.offerGeneration += 1;
      this.negotiationPending = false;
      this.forgetLocalIceNegotiation(this.localOfferId);
      this.localOfferId = null;
      this.localIceNegotiationId = null;
      this.peerRefreshRecoveryPending = false;
      await connection.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
      if (!this.isActiveConnection(connection)) {
        return;
      }
      this.remoteDescriptionId = payload.negotiationId;
      this.rememberAcceptedRemoteNegotiation(payload.negotiationId);
      this.discardPendingCandidatesExcept(payload.negotiationId);
      await this.flushCandidates(payload.negotiationId);
      if (!this.isActiveConnection(connection)) {
        return;
      }
      this.localIceNegotiationId = payload.negotiationId;
      await connection.setLocalDescription();
      if (!this.isActiveConnection(connection)) {
        return;
      }
      const description = connection.localDescription;
      if (description?.type !== 'answer' || !description.sdp) {
        throw new Error('Browser did not produce an answer description.');
      }
      this.rememberLocalIceUfrags(description.sdp, payload.negotiationId);
      this.options.signaling.send('peer.answer', {
        negotiationId: payload.negotiationId,
        sdp: description.sdp,
      });
      writeLog(this.logger, 'debug', 'webrtc', 'answer.sent', {
        negotiationId: payload.negotiationId,
      });
    } catch (cause) {
      this.fail(
        'remote-description-failed',
        'Could not apply the remote WebRTC offer.',
        cause,
        true,
      );
    }
  }

  private async handleAnswer(payload: ProtocolPayloads['peer.answer']): Promise<void> {
    if (payload.negotiationId !== this.localOfferId) {
      writeLog(this.logger, 'warn', 'webrtc', 'answer.ignored', {
        negotiationId: payload.negotiationId,
        reason: 'stale',
      });
      return;
    }

    const connection = this.requireConnection();
    this.isSettingRemoteAnswerPending = true;
    try {
      await connection.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      if (!this.isActiveConnection(connection)) {
        return;
      }
      this.remoteDescriptionId = payload.negotiationId;
      this.discardPendingCandidatesExcept(payload.negotiationId);
      await this.flushCandidates(payload.negotiationId);
      if (!this.isActiveConnection(connection)) {
        return;
      }
      this.localOfferId = null;
      writeLog(this.logger, 'debug', 'webrtc', 'answer.applied', {
        negotiationId: payload.negotiationId,
      });
      this.maybeMakeOffer();
    } catch (cause) {
      this.fail(
        'remote-description-failed',
        'Could not apply the remote WebRTC answer.',
        cause,
        true,
      );
    } finally {
      this.isSettingRemoteAnswerPending = false;
    }
  }

  private readonly handleIceCandidate = (event: RTCPeerConnectionIceEvent) => {
    const ufrag = event.candidate?.usernameFragment;
    const negotiationId =
      ufrag === undefined || ufrag === null
        ? this.localIceNegotiationId
        : (this.localIceUfrags.get(ufrag) ?? null);
    if (negotiationId === null || this._state === 'closed') {
      return;
    }
    const candidate = normalizeLocalCandidate(event.candidate);
    try {
      this.options.signaling.send('peer.ice', { negotiationId, candidate });
      writeLog(this.logger, 'debug', 'webrtc', 'ice.sent', {
        negotiationId,
        completed: candidate === null,
      });
      if (candidate === null && this.localIceNegotiationId === negotiationId) {
        this.localIceNegotiationId = null;
      }
    } catch (cause) {
      this.fail('signaling-failed', 'Could not send an ICE candidate.', cause, true);
    }
  };

  private async handleRemoteCandidate(message: PeerIceMessage): Promise<void> {
    const { candidate, negotiationId } = message.payload;
    if (this.ignoredNegotiations.has(negotiationId)) {
      return;
    }
    if (
      this.acceptedRemoteNegotiations.has(negotiationId) &&
      this.remoteDescriptionId !== negotiationId
    ) {
      return;
    }
    if (this.remoteDescriptionId !== negotiationId) {
      this.queueCandidate(negotiationId, candidate);
      return;
    }
    await this.addCandidate(candidate, negotiationId);
  }

  private queueCandidate(negotiationId: string, candidate: IceCandidatePayload | null): void {
    const total = [...this.pendingCandidates.values()].reduce(
      (sum, values) => sum + values.length,
      0,
    );
    if (total >= MAX_PENDING_CANDIDATES) {
      this.fail(
        'ice-candidate-failed',
        'Too many ICE candidates arrived before SDP.',
        undefined,
        false,
      );
      return;
    }
    const pending = this.pendingCandidates.get(negotiationId) ?? [];
    pending.push(candidate);
    this.pendingCandidates.set(negotiationId, pending);
  }

  private async flushCandidates(negotiationId: string): Promise<void> {
    const candidates = this.pendingCandidates.get(negotiationId) ?? [];
    this.pendingCandidates.delete(negotiationId);
    for (const candidate of candidates) {
      await this.addCandidate(candidate, negotiationId);
    }
  }

  private async addCandidate(
    candidate: IceCandidatePayload | null,
    negotiationId: string,
  ): Promise<void> {
    if (this._state === 'closed') {
      return;
    }
    try {
      await this.requireConnection().addIceCandidate(toIceCandidateInit(candidate));
      writeLog(this.logger, 'debug', 'webrtc', 'ice.applied', {
        negotiationId,
        completed: candidate === null,
      });
    } catch (cause) {
      this.fail('ice-candidate-failed', 'Could not apply a remote ICE candidate.', cause, false);
    }
  }

  private readonly handleTrack = (event: RTCTrackEvent) => {
    this.emit('remotetrack', {
      track: event.track,
      streams: [...event.streams],
      transceiver: event.transceiver,
    });
  };

  private readonly handleConnectionStateChange = () => {
    const connection = this.peerConnection;
    if (connection === null) {
      return;
    }
    this.emit('connectionstatechange', { state: connection.connectionState });
    switch (connection.connectionState) {
      case 'connecting':
        this.transition('peer-connecting');
        break;
      case 'connected':
        this.remoteReady = true;
        this.peerRefreshRecoveryPending = false;
        this.transition('peer-connected');
        this.maybeMakeOffer();
        break;
      case 'disconnected':
        this.transition('peer-disconnected');
        break;
      case 'failed':
        this.transition('peer-failed');
        this.fail('negotiation-failed', 'The WebRTC connection failed.', undefined, true);
        break;
      case 'new':
      case 'closed':
        break;
    }
  };

  private readonly handleIceConnectionStateChange = () => {
    const state = this.peerConnection?.iceConnectionState;
    if (state !== undefined) {
      this.emit('iceconnectionstatechange', { state });
      writeLog(this.logger, 'info', 'webrtc', 'ice-connection.state', { state });
    }
  };

  private readonly handleIceGatheringStateChange = () => {
    const state = this.peerConnection?.iceGatheringState;
    if (state !== undefined) {
      this.emit('icegatheringstatechange', { state });
    }
  };

  private readonly handleSignalingStateChange = () => {
    const state = this.peerConnection?.signalingState;
    if (state !== undefined) {
      this.emit('signalingstatechange', { state });
    }
  };

  private addConnectionListeners(connection: RTCPeerConnection): void {
    connection.addEventListener('negotiationneeded', this.handleNegotiationNeeded);
    connection.addEventListener('icecandidate', this.handleIceCandidate);
    connection.addEventListener('track', this.handleTrack);
    connection.addEventListener('connectionstatechange', this.handleConnectionStateChange);
    connection.addEventListener('iceconnectionstatechange', this.handleIceConnectionStateChange);
    connection.addEventListener('icegatheringstatechange', this.handleIceGatheringStateChange);
    connection.addEventListener('signalingstatechange', this.handleSignalingStateChange);
  }

  private removeConnectionListeners(connection: RTCPeerConnection): void {
    connection.removeEventListener('negotiationneeded', this.handleNegotiationNeeded);
    connection.removeEventListener('icecandidate', this.handleIceCandidate);
    connection.removeEventListener('track', this.handleTrack);
    connection.removeEventListener('connectionstatechange', this.handleConnectionStateChange);
    connection.removeEventListener('iceconnectionstatechange', this.handleIceConnectionStateChange);
    connection.removeEventListener('icegatheringstatechange', this.handleIceGatheringStateChange);
    connection.removeEventListener('signalingstatechange', this.handleSignalingStateChange);
  }

  private attachInitialMedia(connection: RTCPeerConnection, media: LocalMediaTracks): void {
    this.addInitialTrack(connection, 'video', media.video, media.screenStream);
    this.addInitialTrack(connection, 'screen-audio', media.screenAudio, media.screenStream);
    this.addInitialTrack(connection, 'microphone', media.microphone, media.microphoneStream);
  }

  private addInitialTrack(
    connection: RTCPeerConnection,
    source: LocalMediaSource,
    track: MediaStreamTrack | undefined,
    stream: MediaStream | undefined,
  ): void {
    if (track === undefined) {
      return;
    }
    const sender = stream ? connection.addTrack(track, stream) : connection.addTrack(track);
    this.senders.set(source, sender);
  }

  private async syncTrack(
    connection: RTCPeerConnection,
    source: LocalMediaSource,
    track: MediaStreamTrack | undefined,
    stream: MediaStream | undefined,
  ): Promise<void> {
    const sender = this.senders.get(source);
    if (sender !== undefined) {
      await sender.replaceTrack(track ?? null);
      return;
    }
    if (track !== undefined) {
      this.addInitialTrack(connection, source, track, stream);
    }
  }

  private async performClose(options: {
    readonly notifyPeer?: boolean;
    readonly reason?: PeerLeaveReason;
  }): Promise<void> {
    if (this._state === 'closed') {
      return;
    }
    this.transition('close');

    if (options.notifyPeer !== false) {
      try {
        this.options.signaling.send('peer.leave', { reason: options.reason ?? 'left' });
      } catch {
        // Signaling may already be gone; local resource release must still complete.
      }
    }

    this.unsubscribeSignaling?.();
    this.unsubscribeSignaling = null;
    const connection = this.peerConnection;
    this.peerConnection = null;
    if (connection !== null) {
      this.removeConnectionListeners(connection);
      await Promise.allSettled(
        [...this.senders.values()].map((sender) => sender.replaceTrack(null)),
      );
      connection.close();
    }
    this.senders.clear();
    this.pendingCandidates.clear();
    this.ignoredNegotiations.clear();
    this.acceptedRemoteNegotiations.clear();
    this.localIceUfrags.clear();
    this.offerGeneration += 1;
    this.iceRestartPromise = null;
    this.peerRefreshRecoveryPending = false;
    this.remoteSignalingEpoch = -1;
    writeLog(this.logger, 'info', 'webrtc', 'peer.closed', {});
    this.clearListeners();
  }

  private rememberIgnoredNegotiation(negotiationId: string): void {
    this.ignoredNegotiations.add(negotiationId);
    while (this.ignoredNegotiations.size > MAX_IGNORED_NEGOTIATIONS) {
      const oldest = this.ignoredNegotiations.values().next().value as string | undefined;
      if (oldest === undefined) {
        break;
      }
      this.ignoredNegotiations.delete(oldest);
    }
  }

  private rememberAcceptedRemoteNegotiation(negotiationId: string): void {
    this.acceptedRemoteNegotiations.add(negotiationId);
    while (this.acceptedRemoteNegotiations.size > MAX_IGNORED_NEGOTIATIONS) {
      const oldest = this.acceptedRemoteNegotiations.values().next().value as string | undefined;
      if (oldest === undefined) break;
      this.acceptedRemoteNegotiations.delete(oldest);
    }
  }

  private discardPendingCandidatesExcept(negotiationId: string): void {
    for (const pendingId of this.pendingCandidates.keys()) {
      if (pendingId !== negotiationId) {
        this.pendingCandidates.delete(pendingId);
        this.rememberIgnoredNegotiation(pendingId);
      }
    }
  }

  private rememberLocalIceUfrags(sdp: string, negotiationId: string): void {
    for (const match of sdp.matchAll(/^a=ice-ufrag:([^\r\n]+)$/gmu)) {
      const ufrag = match[1];
      if (ufrag) this.localIceUfrags.set(ufrag, negotiationId);
    }
    while (this.localIceUfrags.size > MAX_IGNORED_NEGOTIATIONS) {
      const oldest = this.localIceUfrags.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.localIceUfrags.delete(oldest);
    }
  }

  private forgetLocalIceNegotiation(negotiationId: string | null): void {
    if (negotiationId === null) return;
    for (const [ufrag, owner] of this.localIceUfrags) {
      if (owner === negotiationId) this.localIceUfrags.delete(ufrag);
    }
  }

  private requireConnection(): RTCPeerConnection {
    if (this.peerConnection === null || this._state === 'closed') {
      throw new WebRtcError('invalid-state', 'Peer connection is not active.');
    }
    return this.peerConnection;
  }

  private isActiveConnection(connection: RTCPeerConnection): boolean {
    return this.peerConnection === connection && this._state !== 'closed';
  }

  private transition(cause: WebRtcStateCause): void {
    const previous = this._state;
    const current = nextWebRtcState(previous, cause);
    if (previous === current) {
      return;
    }
    this._state = current;
    writeLog(this.logger, 'info', 'webrtc', 'state.changed', { previous, current, cause });
    this.emit('statechange', { previous, current, cause });
  }

  private fail(code: WebRtcErrorCode, message: string, cause: unknown, fatal: boolean): void {
    if (this._state === 'closed') {
      return;
    }
    const error = new WebRtcError(code, message, cause === undefined ? undefined : { cause });
    writeLog(this.logger, fatal ? 'error' : 'warn', 'webrtc', 'operation.failed', {
      code,
      fatal,
      state: this._state,
    });
    this.emit('error', { error, fatal });
    if (fatal) {
      this.transition('peer-failed');
    }
  }
}

function configureRelayOnly(connection: RTCPeerConnection): boolean {
  const configuration = connection.getConfiguration();
  if (!hasTurnServer(configuration.iceServers ?? [])) return false;
  if (configuration.iceTransportPolicy !== 'relay') {
    connection.setConfiguration({ ...configuration, iceTransportPolicy: 'relay' });
  }
  return true;
}

function hasTurnServer(servers: readonly RTCIceServer[]): boolean {
  return servers.some(({ urls }) =>
    (typeof urls === 'string' ? [urls] : urls).some(
      (url) => url.startsWith('turn:') || url.startsWith('turns:'),
    ),
  );
}

function validateLocalMedia(media: LocalMediaTracks): void {
  validateTrack('video', media.video, 'video');
  validateTrack('screenAudio', media.screenAudio, 'audio');
  validateTrack('microphone', media.microphone, 'audio');
  const tracks = [media.video, media.screenAudio, media.microphone].filter(
    (track): track is MediaStreamTrack => track !== undefined,
  );
  if (new Set(tracks).size !== tracks.length) {
    throw new WebRtcError('invalid-track', 'The same media track cannot serve multiple sources.');
  }
}

function validateTrack(
  source: string,
  track: MediaStreamTrack | undefined,
  expectedKind: 'audio' | 'video',
): void {
  if (track !== undefined && (track.kind !== expectedKind || track.readyState === 'ended')) {
    throw new WebRtcError('invalid-track', `${source} must be a live ${expectedKind} track.`);
  }
}

function normalizeLocalCandidate(candidate: RTCIceCandidate | null): IceCandidatePayload | null {
  if (candidate === null || candidate.candidate === '') {
    return null;
  }
  const value: IceCandidatePayload = {
    candidate: candidate.candidate,
    sdpMLineIndex: candidate.sdpMLineIndex,
    sdpMid: candidate.sdpMid,
    ...(candidate.usernameFragment === undefined
      ? {}
      : { usernameFragment: candidate.usernameFragment }),
  };
  return value;
}

function toIceCandidateInit(candidate: IceCandidatePayload | null): RTCIceCandidateInit | null {
  if (candidate === null) {
    return null;
  }
  return {
    candidate: candidate.candidate,
    sdpMLineIndex: candidate.sdpMLineIndex,
    sdpMid: candidate.sdpMid,
    ...(candidate.usernameFragment === undefined
      ? {}
      : { usernameFragment: candidate.usernameFragment }),
  };
}
